import Controller from '@ember/controller';
import { all } from 'rsvp';

import { task, timeout, rawTimeout } from 'ember-concurrency';
import { inject as controller } from '@ember/controller';
import { inject as service } from '@ember/service';
import { action, get, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';

import { beebeeAjaxGet } from 'beebee-base/lib/utility/ajax';

import getConfig from 'beebee-base/lib/get-config';

import {
  checkRequiredFiscalFields,
  completaStraniero as completaStranieroFor,
} from 'e-commerce/lib/fiscal-contact-validation';
import {
  PAYMENT_OUTCOME,
  PAYMENT_POLL_INTERVAL_MS,
  PAYMENT_POLL_MAX_ATTEMPTS,
  canRetryPayment,
  paymentOutcome,
} from 'e-commerce/lib/payment-outcome';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      controller — standalone deep-link page for a `/payment_id`+`/amount`
 *              payment request sent to a contact (e.g. via `sync_relations`/
 *              request_payment from checkout.js). Loads the payment, lets the contact
 *              complete/confirm their fiscal data, and offers a retry-payment path.
 *   extends:   Controller (@ember/controller)
 *   template:  none
 *   qp:        payment_id, amount
 *   key:       initPageTask (authenticates from the deep-link token, kicks
 *              loadPaymentTask + applicationController.loadMe), loadPaymentTask
 *              (/payments/:id), retryPaymentTask (clones a refused payment into a new
 *              draft with the same projects/events/contacts), saveContactTask (fiscal
 *              save via applicationController.saveRecordRelated), checkRequired/
 *              completaStraniero (thin wrappers over lib/fiscal-contact-validation,
 *              shared with checkout.js — they only resolve the contact),
 *              outcome / isChecking / canRetry (lib/payment-outcome, read off
 *              `paymentRecord`), pollPaymentTask (re-reads the payment while the
 *              bank's answer is pending; sets verifyTimedOut when it gives up).
 *   uses:      applicationController (@controller('application') — gdprField, loadMe,
 *              saveRecordRelated, sendInvitation), lib/fiscal-contact-validation,
 *              lib/utility/ajax, lib/get-config
 *   siblings:  routes/payment-request.js (kicks initPageTask on entry, cancels on
 *              leave), checkout.js (shares lib/fiscal-contact-validation)
 *   tests:     payment-request-canbuy-test.js, payment-request-tasks-test.js
 *   gotchas:   retryPaymentTask and saveContactTask both call
 *              applicationController.saveRecordRelated, which never rejects — it
 *              resolves `false` on a refused save. Both check `=== false` explicitly
 *              and roll back / bail before doing anything that assumes the save
 *              succeeded (retryPaymentTask rolls back the draft payment;
 *              saveContactTask skips the reloadMeTask that would otherwise overwrite
 *              the just-typed fiscal data with stale server state).
 *              The outcome reads `paymentRecord` = the loaded `payment`, falling back
 *              to `content`: this route's model is the raw query-param object, so the
 *              former `content.success` / `content.payment_method` reads were always
 *              undefined and every paid request showed «checking» + «Riproponi
 *              pagamento» (a second charge one click away). No retry while checking.
 *
 * Deep-linked payment-completion page for a contact who received a payment
 * request outside the normal checkout flow.
 */
export default class PaymentRequestController extends Controller {
  @controller('application') applicationController;

  @service session;

  @service notify;
  @service store;
  @service intl;
  @service log;

  queryParams = ['payment_id', 'amount'];

  @tracked payment_id;
  @tracked amount;

  @tracked reloadCheck = false;
  @tracked checkTerms = false;

  @tracked termAccepted;
  @tracked dataAvailable;
  @tracked payment;
  @tracked emptyContactData;

  // Set by pollPaymentTask when the bank's answer did not arrive within the poll window.
  @tracked verifyTimedOut = false;

  pollIntervalMs = PAYMENT_POLL_INTERVAL_MS;
  pollMaxAttempts = PAYMENT_POLL_MAX_ATTEMPTS;

  // The payment the outcome is about: the record loadPaymentTask / retryPaymentTask set.
  // `content` is only a fallback (the route model is the query-param object, not a payment).
  get paymentRecord() {
    return this.payment || this.content || null;
  }

  get isBonifico() {
    return this.paymentRecord?.payment_method === 'Bonifico';
  }

  get outcome() {
    return paymentOutcome({
      success: !!this.paymentRecord?.success,
      failed: this.pagamentoVerificato,
      bonifico: this.isBonifico,
      timedOut: this.verifyTimedOut,
    });
  }

  // «checking» only once a method was chosen: before that the page shows PaymentMethod.
  get isChecking() {
    return !!this.paymentRecord?.payment_method && this.outcome === PAYMENT_OUTCOME.CHECKING;
  }

  get canRetry() {
    return canRetryPayment(this.outcome);
  }

  get not_required_field() {
    return this.session.option_field?.ecommerce_not_required_field || [];
  }

  get generalTerms() {
    return this.session.option_field?.ecommerce_general_term || null;
  }

  get canBuy() {
    if (!this.generalTerms) {
      return true;
    }

    if (!this.termAccepted) {
      return false;
    }

    let requiredActive = true;
    const termOptions = this.applicationController.get('gdprField.organization_term_options.data');

    if (Array.isArray(termOptions)) {
      termOptions.forEach((d) => {
        // Same invalidation contract as checkout.js `canBuy` — the consent
        // rows are plain objects written by `(mut c.active)` (= `set()`), so
        // the read has to go through `get` for this getter to recompute when
        // the customer ticks a required consent. See checkout.js for the full
        // mechanism.
        if (get(d, 'required') && get(d, 'registration') && !get(d, 'active')) {
          requiredActive = false;
        }
      });
    }

    return requiredActive;
  }

  get pagamentoVerificato() {
    const paymentMethod = this.paymentRecord?.payment_method;
    const transactionType = this.paymentRecord?.transaction_type;

    return paymentMethod === 'Bonifico' ? false : !!transactionType;
  }

  @task({ drop: true }) *initPageTask() {
    if (!this.session.token || !this.payment_id) {
      return;
    }

    this.session.authenticate(this.session.token, null, false);
    yield timeout(150);
    this.loadPaymentTask.perform();
    this.applicationController.loadMe.perform();
  }

  @task({ drop: true }) *loadPaymentTask() {
    var self = this;

    set(this, 'dataAvailable', false);

    try {
      const payment = yield beebeeAjaxGet(
        `${getConfig(this).APP.urlApi}/payments/${this.payment_id}`,
        self.session.token
      );

      self.store.pushPayload(payment);
      const paymentStore = self.store.peekRecord('payment', this.payment_id);

      set(this, 'payment', paymentStore);

      set(this, 'dataAvailable', true);
      this.checkEmptyContact();
      // A method already chosen and no bank answer yet: watch for it instead of offering a retry.
      if (this.isChecking) this.pollPaymentTask.perform();
    } catch (e) {
      this.log.error(e);
      this.notify.error(this.intl.t('beebeeboard error'));
    }
  }

  /**
   * Same contract as payment-status pollPaymentTask: while the outcome is «checking»,
   * re-read the payment (pushPayload onto the same record, so the page updates in place;
   * dataAvailable is NOT toggled, unlike loadPaymentTask) until the bank answers or the
   * window closes, then `verifyTimedOut` offers the warned retry. Ids captured first.
   */
  @task({ restartable: true }) *pollPaymentTask() {
    const paymentId = this.payment?.id ?? this.payment_id;
    const token = this.session.token;
    this.verifyTimedOut = false;
    if (!paymentId) return;

    for (let attempt = 0; attempt < this.pollMaxAttempts; attempt++) {
      if (!this.isChecking) return;
      yield rawTimeout(this.pollIntervalMs);
      try {
        const fresh = yield beebeeAjaxGet(`${getConfig(this).APP.urlApi}/payments/${paymentId}`, token);
        if (fresh) this.store.pushPayload(fresh);
      } catch (e) {
        // One missed look, not an outcome: keep polling until the window closes.
        this.log.warn('payment-request: payment re-read failed', e);
      }
    }

    if (this.isChecking) this.verifyTimedOut = true;
  }

  checkEmptyContact() {
    let checkField = false;

    if (this.session.me?.id) {
      checkField = this.checkRequired(false);
    }

    set(this, 'emptyContactData', checkField);
  }

  @task({ drop: true }) *retryPaymentTask() {
    // No second payment while the bank is still answering (a charge may be under way):
    // the template offers no retry then; this refuses a stale click and says why.
    if (this.isChecking) {
      this.notify.info(this.intl.t('ec.payment.checking_no_retry'));
      return;
    }
    let newPayment = null;
    try {
      newPayment = this.store.createRecord('payment', {
        sent: false,
        date: this.payment.get('date'),
        total: this.payment.get('total'),
        option_json: this.payment.get('option_json'),
        transaction_type: this.payment.get('transaction_type'),
        success: false,
      });

      // Assicurati che contacts, projects ed events siano caricati asincronamente anche per newPayment
      const [projects, events, contacts] = yield all([
        this.payment.get('projects'),
        this.payment.get('events'),
        this.payment.get('contacts'),
      ]);

      const [newProjects, newEvents, newContacts] = yield all([
        newPayment.get('projects'),
        newPayment.get('events'),
        newPayment.get('contacts'),
      ]);

      for (const i of (projects || []).slice()) {
        newProjects.push(i);
      }

      for (const i of (events || []).slice()) {
        newEvents.push(i);
      }

      for (const i of (contacts || []).slice()) {
        newContacts.push(i);
      }

      const item = yield this.applicationController.saveRecordRelated(newPayment);
      //this.store.pushPayload(item[0]);

      // saveRecordRelated NEVER rejects — it resolves `false` on failure, so the catch
      // below never fires for a refused save and its rollback never ran: the unsaved
      // retry `payment` stayed in the store as an isNew record, ready to be swept into
      // a later related save.
      if (item === false) {
        if (!newPayment.isDestroyed && !newPayment.isDestroying) {
          newPayment.rollbackAttributes();
        }
        return;
      }

      if (item) {
        const savedId = item[0]?.data?.id;
        if (savedId) {
          const paymentSaved = this.store.peekRecord('payment', savedId);
          set(this, 'payment', paymentSaved);
        }
      }
    } catch (error) {
      // Roll back the unsaved retry payment so it doesn't orphan in the store.
      if (newPayment && !newPayment.isDestroyed && !newPayment.isDestroying) {
        newPayment.rollbackAttributes();
      }
      this.log.error('Error retrying payment:', error);
      this.notify.error(this.intl.t('beebeeboard error'));
    }
  }

  @task({ drop: true }) *saveContactTask() {
    const meId = this.session.me?.id;
    const contact = this.store.peekRecord('contact', meId);

    if (this.checkRequired()) {
      // Inverti il valore di 'my_dirty'
      set(contact, 'my_dirty', !contact.get('my_dirty'));

      // Salva il record associato.
      // saveRecordRelated NEVER rejects — it resolves `false` on failure. reloadMeTask
      // below refetches `me` from the server, so running it after a refused save
      // overwrote the billing/fiscal data the customer had just typed with stale
      // server state. Strictly `=== false`: that is the only refusal signal.
      const saved = yield this.applicationController.saveRecordRelated(contact);
      if (saved === false) {
        return;
      }

      // Ricarica il contatto 'me' e aggiorna i dati nel controller
      yield this.session.reloadMeTask.perform();

      // Controlla se il contatto è vuoto
      this.checkEmptyContact();

      // Scorri la pagina verso l'alto
      window.scrollTo({ top: 100, behavior: 'smooth' });
    } else {
      this.notify.warning(this.intl.t('missing mandatory fields'));
    }
  }

  checkRequired(showError = true) {
    return checkRequiredFiscalFields(this.store.peekRecord('contact', this.session.me?.id), {
      notRequired: this.not_required_field,
      showError,
    });
  }

  completaStraniero() {
    completaStranieroFor(this.store.peekRecord('contact', this.session.me?.id));
  }

  @action
  sendInvitation() {
    this.applicationController.sendInvitation.perform();
  }
}
